import { useMemo, useState, type KeyboardEvent, type ReactNode } from "react"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { Check, ChevronsUpDown, Search } from "lucide-react"

export interface SearchableSelectOption {
  value: string
  label: string
  /** Dettaglio mostrato accanto all'etichetta, escluso dalla ricerca. */
  hint?: string
}

interface SearchableSelectProps {
  value: string
  onValueChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  disabled?: boolean
  /**
   * Ricerca lato server: riceve il testo digitato e le `options` arrivano già
   * filtrate dal chiamante, senza filtro locale.
   */
  onSearchChange?: (search: string) => void
  /** Etichetta del valore scelto quando non è tra le `options` correnti. */
  selectedLabel?: string
  /** Contenuto sotto l'elenco (es. quanti risultati non sono mostrati). */
  footer?: ReactNode
  className?: string
  "aria-label"?: string
}

export function SearchableSelect({
  value,
  onValueChange,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyText = "No results",
  disabled = false,
  onSearchChange,
  selectedLabel,
  footer,
  className,
  "aria-label": ariaLabel,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [highlight, setHighlight] = useState(0)

  const selected = options.find((option) => option.value === value)
  const triggerLabel = selected?.label ?? selectedLabel

  const filtered = useMemo(() => {
    if (!search || onSearchChange) return options
    const query = search.toLowerCase()
    return options.filter((option) => option.label.toLowerCase().includes(query))
  }, [options, search, onSearchChange])

  const updateSearch = (next: string) => {
    setSearch(next)
    setHighlight(0)
    onSearchChange?.(next)
  }

  const resetSearch = () => updateSearch("")

  const selectOption = (optionValue: string) => {
    onValueChange(optionValue)
    setOpen(false)
    resetSearch()
  }

  const handleOpenChange = (next: boolean) => {
    setOpen(next)
    resetSearch()
  }

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setHighlight((current) =>
        filtered.length === 0 ? 0 : Math.min(current + 1, filtered.length - 1),
      )
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setHighlight((current) => Math.max(current - 1, 0))
    } else if (event.key === "Enter") {
      event.preventDefault()
      const option = filtered[highlight]
      if (option) selectOption(option.value)
    }
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", className)}
        >
          <span className={cn("truncate", !triggerLabel && "text-muted-foreground")}>
            {triggerLabel ?? placeholder}
          </span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0"
        align="start"
      >
        <div className="flex items-center border-b px-3">
          <Search className="h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          <Input
            value={search}
            onChange={(event) => updateSearch(event.target.value)}
            onKeyDown={handleSearchKeyDown}
            placeholder={searchPlaceholder}
            className="h-9 border-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
        </div>
        <div className="max-h-60 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {emptyText}
            </p>
          ) : (
            filtered.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onClick={() => selectOption(option.value)}
                className={cn(
                  "flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground",
                  index === highlight && "bg-accent text-accent-foreground",
                )}
              >
                <span className="truncate">{option.label}</span>
                <span className="flex shrink-0 items-center gap-2">
                  {option.hint && (
                    <span className="text-xs text-muted-foreground">{option.hint}</span>
                  )}
                  {option.value === value && <Check className="h-4 w-4" />}
                </span>
              </button>
            ))
          )}
        </div>
        {footer && (
          <div className="border-t px-3 py-2 text-xs text-muted-foreground">{footer}</div>
        )}
      </PopoverContent>
    </Popover>
  )
}
